import React, { ComponentProps, FC, memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from '@pegasus/utils';
import { OpenCampaignCard } from '@browsing/campaign';
import { useConfig } from '@browsing/config';
import { trackSelectPromotion, trackViewPromotion } from '@browsing/tracking';
import { ErrorBoundary } from '@browsing/components';

import { getCategoryTabId, getPlusExclusiveCampaign } from '@client/redux/modules/mylounge';

type PlusExclusiveCampaignProps = {
    aspectRatio: ComponentProps<typeof OpenCampaignCard>['aspectRatio'];
    grid?: ComponentProps<typeof OpenCampaignCard>['grid'];
    position: ComponentProps<typeof OpenCampaignCard>['position'];
    withMobileSpacing?: ComponentProps<typeof OpenCampaignCard>['withMobileSpacing'];
};

const PlusExclusiveCampaignInternal: FC<PlusExclusiveCampaignProps> = memo(
    ({ aspectRatio, grid, position, withMobileSpacing }) => {
        const config = useConfig();

        const categoryTabId = useSelector(getCategoryTabId);
        const exclusiveCampaign = useSelector(getPlusExclusiveCampaign);

        useEffect(() => {
            if (!exclusiveCampaign) {
                return;
            }

            if (!config.isEnabled('ga4')) {
                return;
            }

            const index = position?.split('|')[0];

            trackViewPromotion({
                element: 'plus_exclusive_banner',
                creativeName: 'plus_exclusive',
                itemListName: 'plus_exclusive',
                items: [
                    {
                        index: isMobile() ? Number(index) : Number(index) - 1,
                        isExclusiveItem: 1,
                        campaignIdentifier: exclusiveCampaign.campaignId,
                    },
                ],
            });
        }, [exclusiveCampaign, position, config, categoryTabId]);

        const track = useCallback(() => {
            if (!exclusiveCampaign) {
                return;
            }

            const index = position?.split('|')[0];

            trackSelectPromotion({
                element: 'plus_exclusive_banner',
                creativeName: 'plus_exclusive',
                itemListName: 'plus_exclusive',
                item: {
                    campaignIdentifier: exclusiveCampaign.campaignId,
                    categoryId: categoryTabId,
                    index: isMobile() ? Number(index) : Number(index) - 1,
                    isExclusiveItem: 1,
                },
            });
        }, [exclusiveCampaign, position, categoryTabId]);

        if (!exclusiveCampaign) {
            return null;
        }

        return (
            <OpenCampaignCard
                aspectRatio={aspectRatio}
                campaign={exclusiveCampaign}
                grid={grid}
                onCampaignClick={track}
                position={position}
                withMobileSpacing={withMobileSpacing}
            />
        );
    },
);

export const PlusExclusiveCampaign: FC<PlusExclusiveCampaignProps> = props => (
    <ErrorBoundary componentName="PlusExclusiveCampaign">
        <PlusExclusiveCampaignInternal {...props} />
    </ErrorBoundary>
);
